// Вход официанта: /waiter-login
// 1) QR: /waiter-login?token=… — без пароля (как раньше)
// 2) Без токена: email + пароль (тот же /api/auth/login, только role=waiter)
// 3) На странице подключается manifest-waiter.json для установки PWA «Официант»
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import BrandFooter from '../components/BrandFooter';
import { logoPng } from '../config/assets';
import { STAFF_PWA_KEY } from '../lib/staffPwa';

const base = process.env.PUBLIC_URL || '';

/** Safari / сканеры: query не всегда в useSearchParams */
function readTokenFromLocation() {
  if (typeof window === 'undefined') return '';
  try {
    const q = new URLSearchParams(window.location.search);
    let t = q.get('token');
    if (t) return t.trim();
    const h = window.location.hash || '';
    if (h.includes('token=')) {
      const queryPart = h.includes('?') ? h.split('?')[1] : h.replace(/^#/, '');
      const q2 = new URLSearchParams(queryPart);
      t = q2.get('token');
      if (t) return t.trim();
    }
  } catch { /* ignore */ }
  return '';
}

export default function WaiterLoginPage({ onLogin }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const tokenFromUrl = useMemo(
    () => (searchParams.get('token') || readTokenFromLocation()).trim(),
    [searchParams]
  );
  const hasToken = tokenFromUrl.length > 0;

  const [autoStatus, setAutoStatus] = useState(() => (hasToken ? 'loading' : 'idle'));
  const [autoError, setAutoError] = useState('');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  const [installHint, setInstallHint] = useState(null);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  // PWA: manifest «официант» + заголовок на iOS
  useEffect(() => {
    const main = document.querySelector('link[rel="manifest"]');
    if (!main) return;
    const parent = main.parentNode;
    const prevHref = main.getAttribute('href');
    main.remove();
    const next = document.createElement('link');
    next.rel = 'manifest';
    next.href = `${base}/manifest-waiter.json`;
    parent.appendChild(next);
    const prevTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]')?.getAttribute('content');
    const tMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (tMeta) tMeta.setAttribute('content', 'Официант');
    return () => {
      next.remove();
      const restore = document.createElement('link');
      restore.rel = 'manifest';
      restore.href = prevHref;
      parent.appendChild(restore);
      if (tMeta && prevTitle != null) tMeta.setAttribute('content', prevTitle);
    };
  }, []);

  useEffect(() => {
    const onBip = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onI = () => setInstallHint('ios');
    window.addEventListener('beforeinstallprompt', onBip);
    window.addEventListener('appinstalled', () => { setDeferredPrompt(null); setInstallHint(null); });
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setInstallHint(null);
    } else if (/iPhone|iPad|iPod/.test(navigator.userAgent) && !window.navigator.standalone) {
      onI();
    }
    return () => window.removeEventListener('beforeinstallprompt', onBip);
  }, []);

  // QR / deep link: обмен токена карточки на JWT
  useEffect(() => {
    if (!hasToken) return;
    let cancelled = false;
    setAutoStatus('loading');
    setAutoError('');
    axios
      .post('/api/auth/waiter-login', { token: tokenFromUrl })
      .then(({ data }) => {
        if (cancelled) return;
        try {
          localStorage.removeItem(STAFF_PWA_KEY);
        } catch (_) { /* no-op */ }
        onLogin(data.token, {
          role: data.role,
          restaurant_id: data.restaurant_id,
          email: data.email,
          name: data.name,
        });
        setAutoStatus('ok');
        navigate('/admin', { replace: true });
      })
      .catch((err) => {
        if (cancelled) return;
        setAutoStatus('error');
        setAutoError(err.response?.data?.error || 'Недействительный токен');
      });
    return () => { cancelled = true; };
  }, [hasToken, tokenFromUrl, onLogin, navigate]);

  const handleEmailLogin = useCallback(
    async (e) => {
      e.preventDefault();
      setFormError('');
      setFormLoading(true);
      try {
        const { data } = await axios.post('/api/auth/login', { email, password });
        if (data.role !== 'waiter') {
          setFormError('Эта страница — только для роли «официант». Владелец/админ: вход через «Панель».');
          return;
        }
        try {
          localStorage.removeItem(STAFF_PWA_KEY);
        } catch (_) { /* no-op */ }
        onLogin(data.token, {
          id: data.id,
          role: data.role,
          restaurant_id: data.restaurant_id,
          email: data.email,
          name: data.name,
          is_super_admin: !!data.is_super_admin,
        });
        navigate('/admin', { replace: true });
      } catch (err) {
        setFormError(err.response?.data?.error || 'Ошибка входа');
      } finally {
        setFormLoading(false);
      }
    },
    [email, password, onLogin, navigate]
  );

  const handleInstallClick = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  if (autoStatus === 'ok') return null;

  if (hasToken) {
    return (
      <div style={s.page}>
        <div style={s.pageCenter}>
          <div style={s.card}>
            <div style={s.logo}>
              <img src={logoPng()} alt="" style={s.logoImg} width={64} height={64} />
            </div>
            <h1 style={s.title}>Вход официанта</h1>
            {autoStatus === 'loading' && <p style={s.sub}>Проверка токена...</p>}
            {autoStatus === 'error' && (
              <>
                <div style={s.error}>{autoError}</div>
                <p style={s.hint}>
                  Ссылка с QR устарела или скопирована с ошибкой. Можно войти по email и паролю ниже —
                  кнопка появляется, если у официанта в админке заданы логин и пароль.
                </p>
                {formError && <div style={s.error}>{formError}</div>}
                <EmailForm
                  email={email}
                  password={password}
                  setEmail={setEmail}
                  setPassword={setPassword}
                  onSubmit={handleEmailLogin}
                  formLoading={formLoading}
                  showLoginLink
                />
              </>
            )}
          </div>
        </div>
        <BrandFooter />
      </div>
    );
  }

  // Нет ?token= — нормальный сценарий: пароль (и установка PWA)
  return (
    <div style={s.page}>
      <div style={s.pageCenter}>
        <div style={s.card}>
        <div style={s.logo}>
          <img src={logoPng()} alt="" style={s.logoImg} width={64} height={64} />
        </div>
        <h1 style={s.title}>Вход официанта</h1>
        <p style={s.sub}>
          Закрепите страницу на экране (PWA) и заходите по email и паролю, как в админке.
          QR с персональной ссылкой — по желанию.
        </p>

        {deferredPrompt && (
          <button type="button" onClick={handleInstallClick} style={s.installBtn}>
            📲 Установить «Официант» (PWA)
          </button>
        )}
        {installHint === 'ios' && (
          <p style={s.pwaHint}>
            <strong>На iPhone / iPad:</strong> «Поделиться» → «На экран «Домой»».
          </p>
        )}

        {formError && <div style={s.error}>{formError}</div>}

        <EmailForm
          email={email}
          password={password}
          setEmail={setEmail}
          setPassword={setPassword}
          onSubmit={handleEmailLogin}
          formLoading={formLoading}
          showLoginLink
        />
      </div>
      </div>
      <BrandFooter />
    </div>
  );
}

function EmailForm({
  email, password, setEmail, setPassword, onSubmit, formLoading, showLoginLink,
}) {
  return (
    <form onSubmit={onSubmit} style={s.form}>
      <div style={s.field}>
        <label style={s.label} htmlFor="w-email">Email</label>
        <input
          id="w-email"
          style={s.input}
          type="email"
          value={email}
          autoComplete="username"
          autoFocus
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@restaurant.com"
          required
        />
      </div>
      <div style={s.field}>
        <label style={s.label} htmlFor="w-pass">Пароль</label>
        <input
          id="w-pass"
          style={s.input}
          type="password"
          value={password}
          autoComplete="current-password"
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          required
        />
      </div>
      <button style={s.btn} type="submit" disabled={formLoading}>
        {formLoading ? 'Вход...' : 'Войти'}
      </button>
      {showLoginLink && (
        <p style={s.linkRow}>
          <Link to="/login" style={s.link}>
            Вход в панель владельца
          </Link>
        </p>
      )}
    </form>
  );
}

const s = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f5f5f7',
    fontFamily: '-apple-system, sans-serif',
  },
  pageCenter: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '24px',
    padding: '40px 32px',
    width: '100%',
    maxWidth: '400px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  },
  logo: { marginBottom: '16px', textAlign: 'center' },
  logoImg: { display: 'block', margin: '0 auto', objectFit: 'contain' },
  title: { fontSize: '24px', fontWeight: '700', textAlign: 'center', margin: '0 0 8px' },
  sub: { fontSize: '14px', color: '#666', margin: '0 0 20px', lineHeight: 1.5 },
  hint: { fontSize: '13px', color: '#666', lineHeight: 1.5, marginBottom: '20px' },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '13px', fontWeight: '500', color: '#444' },
  input: {
    padding: '12px 14px',
    borderRadius: '12px',
    border: '1px solid #E0E0E0',
    fontSize: '15px',
    outline: 'none',
    boxSizing: 'border-box',
    width: '100%',
  },
  btn: {
    padding: '14px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: '#000',
    color: '#fff',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px',
  },
  error: {
    padding: '12px',
    borderRadius: '10px',
    backgroundColor: '#FFEBEE',
    color: '#E85D4A',
    fontSize: '14px',
    marginBottom: '8px',
  },
  installBtn: {
    width: '100%',
    marginBottom: '16px',
    padding: '12px',
    borderRadius: '12px',
    border: '1px solid #000',
    background: '#fff',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  pwaHint: { fontSize: '13px', color: '#555', margin: '0 0 16px', lineHeight: 1.45 },
  linkRow: { textAlign: 'center', margin: '8px 0 0' },
  link: { color: '#007AFF', fontSize: '14px' },
};
